import { useLayoutEffect } from 'react';
import posthogJs from 'posthog-js';
import type { CapturedNetworkRequest, PostHog, PostHogConfig } from 'posthog-js';

/**
 * PostHog session replay for signup only (RUT-4433).
 *
 * The webapp starts with recording off (`disable_session_recording`), so the
 * office app, which shows customer data, is never recorded. Signup pages turn
 * it on while they're mounted with `useOnboardingReplay(true)`:
 *   - /onboarding registration (journey steps 1–4, and the invite signup)
 *   - the setup wizard while it's on its company and price steps (2 and 3)
 * The wizard's import and "Get paid" steps (4–5) show customer and bank data,
 * so they are not recorded: the wizard passes `false` there, which stops the
 * recording.
 *
 * Masking applies only to a recording these pages start:
 *   - every input is masked (`maskAllInputs`), password and OTP included
 *   - text inside forms, anything marked `data-replay-mask` (the setup page's
 *     headline, which repeats the company name and email, and its whole
 *     question column), alerts and toasts all show as `***`
 *   - JustiFi's payment web components (card, bank, provisioning) are blocked
 *     outright: they render in shadow DOM, which text masking does not reach
 *   - script tags are blocked: text masking never touches attributes, and
 *     third-party tags (Google Ads' conversion script) put the page URL, query
 *     string and all, in their `src`
 *   - no network bodies or headers, and URLs lose their query string
 *   - no console logs, no canvas
 *
 * Start and stop run in a layout effect: synchronously inside React's commit,
 * before rrweb's MutationObserver callback (a microtask) can serialise the DOM
 * that commit added. With a passive effect, leaving signup for the office app
 * recorded the office app's first frame before the stop ran.
 */
export const REPLAY_MASK_ATTR = 'data-replay-mask';

export const REPLAY_MASK_TEXT_SELECTOR = [
  'form',
  'form *',
  `[${REPLAY_MASK_ATTR}]`,
  `[${REPLAY_MASK_ATTR}] *`,
  '[role="alert"]',
  '[role="alert"] *',
  '[role="status"]',
  '[role="status"] *',
  'ol[tabindex="-1"] li', // Radix toast viewport items
  'ol[tabindex="-1"] li *',
].join(', ');

/**
 * Blocked, never serialised: JustiFi web components used anywhere in the
 * webapp, iframes, and script tags (their `src` can carry the page URL).
 */
export const REPLAY_BLOCK_SELECTOR = [
  'justifi-apple-pay',
  'justifi-bank-account-billing-form-simple',
  'justifi-bank-account-form',
  'justifi-card-billing-form-simple',
  'justifi-card-form',
  'justifi-modular-checkout',
  'justifi-payment-provisioning',
  'justifi-tokenize-payment-method',
  'iframe',
  'script',
  'noscript',
].join(', ');

/** Strip a captured request to method, status, timing and the path. */
export function scrubNetworkRequest(req: CapturedNetworkRequest): CapturedNetworkRequest {
  const name = typeof req.name === 'string' ? req.name.split(/[?#]/)[0] : req.name;
  return {
    ...req,
    name,
    requestBody: null,
    responseBody: null,
    requestHeaders: undefined,
    responseHeaders: undefined,
  };
}

/** Init options merged into the webapp's PostHog init (App.tsx). */
export const REPLAY_INIT_OPTIONS = {
  // Off for the whole app; signup pages start it in code (useOnboardingReplay).
  disable_session_recording: true,
  enable_recording_console_log: false,
  session_recording: {
    maskAllInputs: true,
    maskInputOptions: { password: true, email: true, tel: true, text: true, number: true },
    maskTextSelector: REPLAY_MASK_TEXT_SELECTOR,
    blockSelector: REPLAY_BLOCK_SELECTOR,
    captureCanvas: { recordCanvas: false },
    recordCrossOriginIframes: false,
    recordBody: false,
    recordHeaders: false,
    maskCapturedNetworkRequestFn: scrubNetworkRequest,
  },
} satisfies Partial<PostHogConfig>;

type ReplayClient = Pick<PostHog, 'startSessionRecording' | 'stopSessionRecording'> & {
  __loaded?: boolean;
  config?: { disable_session_recording?: boolean };
};

// How many mounted signup surfaces currently want a recording, so one surface
// stopping can't cut off another that's still on screen. (Registration → wizard
// is not such an overlap: the wizard chunk loads after registration's hook goes
// inactive, so that handover is a stop then a restart in the same session.)
let wanted = 0;

/** Apply the wanted state to PostHog. Safe before init: App's `loaded` calls it again. */
export function syncOnboardingReplay(client: ReplayClient = posthogJs): void {
  if (!client || !client.__loaded) return;
  try {
    // Read what the SDK was told, not `sessionRecordingStarted()`: that stays
    // false while the recorder script is still loading, and a stop skipped in
    // that window would let the recording start on a page that must not record.
    const on = client.config?.disable_session_recording === false;
    if (wanted > 0 && !on) client.startSessionRecording();
    if (wanted === 0 && on) client.stopSessionRecording();
  } catch (error) {
    console.error('[onboardingReplay] PostHog recording toggle failed:', error);
  }
}

/** Record this signup surface while `active` is true and it's mounted. */
export function useOnboardingReplay(active: boolean, client: ReplayClient = posthogJs): void {
  useLayoutEffect(() => {
    if (!active) return;
    wanted += 1;
    syncOnboardingReplay(client);
    return () => {
      wanted = Math.max(0, wanted - 1);
      syncOnboardingReplay(client);
    };
  }, [active, client]);
}

/**
 * Which setup-wizard steps are recorded: the company (journey 2) and the price
 * (3). Import (5) and "Get paid" (4) show customer and bank data: never.
 */
export function wizardStepIsRecorded(journeyStep: number): boolean {
  return journeyStep === 2 || journeyStep === 3;
}

/** Test-only: reset the module counter between tests. */
export function __resetOnboardingReplayForTests(): void {
  wanted = 0;
}
